/**
 * Marketing: campaigns, their audiences and the suppression list.
 *
 * The sidebar group also nests the register, the site scanner and the scanner
 * leads under this heading, but those keep their own top-level routes (see
 * crm/nav.ts) - they were here first and half the app links to them.
 *
 * Everything under this section is an OPERATOR tool. It mails Estonian
 * companies on our own behalf; it is not a newsletter feature a tenant buys.
 * The distinction matters legally, and docs/crm-turundus-plaan.md is where the
 * reasoning lives.
 */
import { Navigate, Route, Routes } from "react-router-dom";
import type { StaffUser } from "../../../lib/auth";
import { OverviewPage } from "./overview";
import { CampaignsList } from "./CampaignsList";
import { CampaignDetail } from "./CampaignDetail";
import { SuppressionPanel } from "./SuppressionPanel";
import { AutomationPage } from "./automation";
import { ContactsPage } from "./contacts";
import { TemplatesList } from "./templates/TemplatesList";
import { TemplateEditor } from "./templates/TemplateEditor";

export function MarketingSection({ user }: { user: StaffUser }) {
  return (
    <Routes>
      {/* The index is the overview, not a redirect to the campaign list. The
          section's own numbers - what has gone out, what came back, and why
          nothing is going out when nothing is - belong above the list of
          letters rather than behind it. */}
      <Route index element={<OverviewPage user={user} />} />
      <Route path="campaigns" element={<CampaignsList user={user} />} />
      <Route path="campaigns/:id" element={<CampaignDetail user={user} />} />
      <Route path="templates" element={<TemplatesList user={user} />} />
      {/* `new` is its own route with its own flag, not `:id === "new"`. The
          static path already wins over the dynamic one in the router's ranking,
          so on this route there IS no `id` param to compare against. */}
      <Route path="templates/new" element={<TemplateEditor user={user} isNew />} />
      <Route path="templates/:id" element={<TemplateEditor user={user} />} />
      {/* The clock over the sender. Its own page rather than a tab on a
          campaign, because everything on it is above every campaign: the hard
          stop, the sender-wide cap and the warm-up ramp all belong to the
          domain, not to one letter's audience. */}
      {/* The addresses and the shared-address report, on one page. Both
          are about mailboxes rather than about one campaign, and the
          exclusion belongs in sight of the thing it excludes from. */}
      <Route path="contacts" element={<ContactsPage user={user} />} />
      <Route path="automation" element={<AutomationPage user={user} />} />
      <Route path="suppression" element={<SuppressionPanel user={user} />} />
      <Route path="*" element={<Navigate to="campaigns" replace />} />
    </Routes>
  );
}
